// ─── Device capability + quality tiering ─────────────────────────────────────
//
// Deliberately does NOT touch WebGL. An earlier version probed capabilities on a
// throwaway canvas context, which is a real hazard: iOS Safari has a hard cap on
// live WebGL contexts and will drop or refuse one, taking the whole canvas black.
// Everything here is derived from navigator/matchMedia only.
//
// GL-dependent decisions (area lights, motion blur) are resolved from the REAL
// renderer instead — see useRenderPath() below, which runs inside <Canvas>.

import { useMemo } from 'react';
import { useThree } from '@react-three/fiber';

const nav = typeof navigator !== 'undefined' ? navigator : {};
const ua  = nav.userAgent || '';

const mq = (q) => {
    try { return typeof matchMedia === 'function' && matchMedia(q).matches; }
    catch { return false; }
};

// ── Form factor ──────────────────────────────────────────────────────────────
const UA_MOBILE = /Android|iPhone|iPod|iPad|Mobile|Silk|Kindle|Opera Mini|IEMobile|BlackBerry/i.test(ua);
// iPadOS 13+ reports a desktop Macintosh UA — the touch point count gives it away
const IPAD_OS   = /Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1;
const COARSE    = mq('(pointer: coarse)') && !mq('(pointer: fine)');

export const IS_MOBILE = UA_MOBILE || IPAD_OS || COARSE;

// ── Perf tier ────────────────────────────────────────────────────────────────
const CORES  = nav.hardwareConcurrency || (IS_MOBILE ? 4 : 8);
// Device Memory API (Chromium only) — GiB of RAM, rounded down to a power of 2
const MEMORY = nav.deviceMemory || 0;
// Save-Data is a decent proxy for a budget device on a metered connection
const SAVE_DATA = !!(nav.connection && nav.connection.saveData);

function resolveTier() {
    if (SAVE_DATA) return 'low';
    if (IS_MOBILE) {
        if (CORES <= 4 || (MEMORY && MEMORY <= 4)) return 'low';
        return 'mid';
    }
    if (CORES <= 2) return 'low';
    return 'high';
}

// ── URL overrides ────────────────────────────────────────────────────────────
// ?quality=low|mid|high forces a tier so the mobile path can be checked from a
// desktop, ?mobile=1 forces the mobile branches, ?debug=1 shows the on-screen
// readout of what this device actually resolved to.
function urlOverrides() {
    try {
        const p = new URLSearchParams(location.search);
        const q = p.get('quality');
        return {
            tier:   (q === 'low' || q === 'mid' || q === 'high') ? q : null,
            mobile: p.get('mobile') === '1',
            debug:  p.get('debug') === '1',
        };
    } catch { return { tier: null, mobile: false, debug: false }; }
}

const OVERRIDE = urlOverrides();

export const PERF_TIER = OVERRIDE.tier || resolveTier();
export const DEBUG     = OVERRIDE.debug;

const MOBILE_LIKE = IS_MOBILE || OVERRIDE.mobile || PERF_TIER !== 'high';

export const QUALITY = {
    tier: PERF_TIER,
    mobile: MOBILE_LIKE,

    // Scene density. The first cut of these was far too aggressive — the field
    // read as empty on a phone. StarField costs nothing per frame (no JS loop at
    // all) and GalaxyField now early-outs while the field is at rest, which on a
    // touch device is essentially always, so the real cost here is point fill rate.
    starCount:   PERF_TIER === 'high' ? 8000 : PERF_TIER === 'mid' ? 6000 : 4000,
    galaxyCount: PERF_TIER === 'high' ? 3500 : PERF_TIER === 'mid' ? 2600 : 1800,
    // Per-particle cursor repulsion is a JS loop over every galaxy particle
    particlePush: PERF_TIER !== 'low',

    // Renderer — these are Canvas props, so they must not depend on the context
    dpr:       PERF_TIER === 'high' ? [1, 2] : PERF_TIER === 'mid' ? [1, 1.5] : 1,
    antialias: PERF_TIER !== 'low',
};

// ─── Live diagnostics ────────────────────────────────────────────────────────
// Filled in once the real renderer exists. Read by the ?debug=1 readout so a
// device can report what it actually resolved to instead of us guessing.
export const DIAG = {
    tier: PERF_TIER,
    mobile: IS_MOBILE,
    cores: CORES,
    memory: MEMORY || '?',
    webgl2: null,
    floatLinear: null,
    gpu: '',
    lighting: '',
    motionBlur: null,
    contextLost: false,
    camDist: 0,
    aspect: 0,
    dpr: 0,
    counts: '',
    label: '',
};

// ─── GL-dependent render path ────────────────────────────────────────────────
// Resolved from the renderer R3F already created — no extra context.
//
// rectAreaLight is the reason the float-linear check exists at all: three.js r158
// always picks the RGBA32F LTC lookup textures on WebGL2, and LINEAR filtering of
// 32-bit float textures is not core WebGL2. Without OES_texture_float_linear the
// lookup texture is incomplete, every sample returns (0,0,0,1), and the area
// lights contribute nothing at all — which is how this scene ended up as a
// near-invisible green blob on mobile, lit only by its teal ambient term.
//
// The decision itself is a pure function of the two capabilities plus the tier, so
// it can be checked without a GPU.
export function resolveRenderPath({ webgl2, floatLinear }) {
    return {
        // Never on a mobile-like device: some drivers advertise the extension but
        // sample it slowly, and the LTC integration is expensive per fragment.
        rectAreaLights: !!floatLinear && !MOBILE_LIKE,
        // The blur costs a full-res offscreen scene render plus a fullscreen blit
        // every frame, blur or no blur.
        motionBlur: PERF_TIER === 'high' && !!webgl2,
    };
}

// Cached per renderer so every consumer gets the identical object — the lighting
// rig, the model's material and the particle shaders must never disagree about
// which path is active, or the scene renders with mismatched assumptions.
let _pathCache = null;
let _pathCacheFor = null;

export function useRenderPath() {
    const { gl } = useThree();

    return useMemo(() => {
        if (_pathCacheFor === gl && _pathCache) return _pathCache;

        let webgl2 = false, floatLinear = false, gpu = '';
        try {
            const ctx = gl.getContext();
            webgl2 = typeof WebGL2RenderingContext !== 'undefined' && ctx instanceof WebGL2RenderingContext;
            floatLinear = gl.extensions.has('OES_texture_float_linear');
            const dbg = ctx.getExtension('WEBGL_debug_renderer_info');
            if (dbg) gpu = String(ctx.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '');
        } catch {
            /* treat anything unreadable as unsupported */
        }

        const path = resolveRenderPath({ webgl2, floatLinear });

        DIAG.webgl2 = webgl2;
        DIAG.floatLinear = floatLinear;
        DIAG.gpu = gpu;
        DIAG.lighting = path.rectAreaLights ? 'rectArea' : 'directional';
        DIAG.motionBlur = path.motionBlur;

        if (!floatLinear) {
            console.info('[quality] OES_texture_float_linear missing — rectAreaLight would render black here; using the directional rig.');
        }

        _pathCache = { ...path, webgl2, floatLinear, gpu };
        _pathCacheFor = gl;
        return _pathCache;
    }, [gl]);
}
